/* eslint-disable react/prop-types */ // TODO: add props
import classNames from 'classnames';

import { SafeHTML } from 'edrolo/domains/app/components/SafeHTML';

/**
 * Modal-style panel that surfaces the AI marker's reasoning for a single
 * student response.
 *
 * The marking agent's structured output is exposed verbatim — there is no
 * paraphrasing or transformation server-side or in this component:
 *
 *   - `ai_feedback`         → the agent's overall feedback string
 *   - `criterion_decisions` → the agent's per-mark-point breakdown,
 *                             each with `criterion`, `awarded_marks`,
 *                             `max_marks`, and `rationale`.
 *
 * Safety:
 * - Both `ai_feedback` and per-criterion `rationale` are LLM-generated text
 *   (potentially containing student-submitted text echoed back). They are
 *   rendered through SafeHTML, which uses DOMPurify to strip any
 *   script/iframe injections.
 * - The wrapping div has `data-mp-mask` so any residual PII the LLM may
 *   have surfaced is not captured by Mixpanel session recordings.
 * - A static banner reminds the teacher this is AI-generated and should
 *   be reviewed before the mark is accepted.
 */
const AIReasoningPanel = ({
  aiMark,
  marksAvailable,
  confidenceBucket,
  onClose,
}) => {
  if (!aiMark) {
    return null;
  }

  const score = Number.isFinite(aiMark.ai_score)
    ? aiMark.ai_score
    : parseInt(aiMark.ai_score, 10);

  const feedbackHtml = aiMark.ai_feedback
    ? aiMark.ai_feedback.replace(/\n/g, '<br />')
    : '';

  const criterionDecisions = Array.isArray(aiMark.criterion_decisions)
    ? aiMark.criterion_decisions
    : [];

  const provider = aiMark.provider || 'unknown';
  const modelName = aiMark.model_name || 'unknown';

  const lastProcessedDisplay = aiMark.last_processed_at
    ? new Date(aiMark.last_processed_at).toLocaleString()
    : 'unknown';

  return (
    <div
      data-testid="ai-reasoning-panel"
      data-mp-mask
      className={classNames(
        'ai-reasoning-panel',
        `ai-reasoning-panel--confidence-${confidenceBucket}`
      )}
    >
      <div className="ai-reasoning-panel__header">
        <h4 className="ai-reasoning-panel__title">
          AI marking explanation — {score}/{marksAvailable}
        </h4>
        <button
          type="button"
          className="ai-reasoning-panel__close btn-link"
          onClick={onClose}
          aria-label="Close AI reasoning"
        >
          ×
        </button>
      </div>

      <div className="ai-reasoning-panel__warning">
        <strong>AI-generated content.</strong> Verify before saving the mark.
        {confidenceBucket === 'low' && (
          <span className="ai-reasoning-panel__low-confidence">
            {' '}
            The model reported low confidence on this response — please review
            carefully.
          </span>
        )}
      </div>

      {criterionDecisions.length > 0 && (
        <div className="ai-reasoning-panel__criteria">
          <h5 className="ai-reasoning-panel__section-title">
            Mark-by-mark breakdown
          </h5>
          <ol className="ai-reasoning-panel__criteria-list">
            {criterionDecisions.map((decision, idx) => {
              const awarded = Number.isFinite(decision.awarded_marks)
                ? decision.awarded_marks
                : parseFloat(decision.awarded_marks);
              const max = Number.isFinite(decision.max_marks)
                ? decision.max_marks
                : parseFloat(decision.max_marks);
              const isAwarded = awarded > 0;
              const rationaleHtml = decision.rationale
                ? String(decision.rationale).replace(/\n/g, '<br />')
                : '';
              return (
                <li
                  key={idx}
                  className={classNames(
                    'ai-reasoning-panel__criterion',
                    isAwarded
                      ? 'ai-reasoning-panel__criterion--awarded'
                      : 'ai-reasoning-panel__criterion--not-awarded'
                  )}
                >
                  <div className="ai-reasoning-panel__criterion-header">
                    <span className="ai-reasoning-panel__criterion-mark">
                      {awarded}/{max}
                    </span>
                    <span className="ai-reasoning-panel__criterion-text">
                      {decision.criterion}
                    </span>
                  </div>
                  {rationaleHtml ? (
                    <SafeHTML
                      as="div"
                      className="ai-reasoning-panel__criterion-rationale"
                    >
                      {rationaleHtml}
                    </SafeHTML>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {feedbackHtml ? (
        <div className="ai-reasoning-panel__overall">
          <h5 className="ai-reasoning-panel__section-title">
            Overall feedback
          </h5>
          <SafeHTML as="div" className="ai-reasoning-panel__feedback">
            {feedbackHtml}
          </SafeHTML>
        </div>
      ) : criterionDecisions.length === 0 ? (
        <p className="ai-reasoning-panel__feedback ai-reasoning-panel__feedback--empty">
          No AI feedback was generated.
        </p>
      ) : null}

      <div className="ai-reasoning-panel__meta">
        <small>
          Generated by {provider}:{modelName} at {lastProcessedDisplay}
        </small>
      </div>
    </div>
  );
};

export default AIReasoningPanel;
